<template>
  <div :class="[classes.listCardWrapper, 'card']">
    <div v-if="useCompetitors" :class="classes.badgeDetails">
      <CardHeaderLeaderboard
        :eventId="eventId"
        style="border-bottom: none"
        :showMarket="true"
        :marketId="marketId"
      />
      <EventName
        :class="[classes.eventBanners, 'event-name']"
        :eventId="eventId"
        :showVs="false"
      />
    </div>
    <div v-else :class="classes.eventDetails">
      <CardHeaderLeaderboard
        :eventId="eventId"
        style="border-bottom: none; justify-content: center"
      />

      <div :class="classes.eventMarketWrapper">
        <EventName
          :class="[classes.eventName, 'event-name']"
          :eventId="eventId"
        />

        <MarketName
          :marketId="market.marketId"
          :fallbackName="market.marketName"
          :class="[classes.marketName, 'market-name']"
        />
      </div>
    </div>

    <div :class="classes.listWrapper">
      <ListOption
        v-for="(option, i) in options"
        :key="option.id"
        :stagger="i"
        :option="option"
        :marketId="market.marketId"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { createUseStyles } from "@fresh8/vue-jss/src";
import { defineComponent } from "vue";
import { injectBrand } from "@fresh8/ad-data-composition/src/brand";

import ListOption from "./../ListOption.vue";
import EventName from "../../../EventNamesBadgesStacked.vue";
import MarketName from "../../../MarketName.vue";
import CardHeaderLeaderboard from "../../../CardHeaderLeaderboard.vue";

import {
  composeMarket,
  composeListMarketOptions,
  composeEvent
} from "@fresh8/ad-data-composition/src/betData";
import { injectCarouselContext } from "../../../../lib/provideInjectors";
import { computed } from "@vue/composition-api";

const useStyles = createUseStyles({
  listCardWrapper: {
    display: "flex",
    flex: "1 1 1px",
    height: "100%",
    border: props => `1px solid ${props.marketBorderColor || "#dcddf"}`,
    background: props => props.cardBackgroundColor,
    borderRadius: props => `${props.borderRadius}px`,
    position: "relative",
    boxSizing: "border-box"
  },
  eventBanners: {
    justifyContent: "space-between !important",
    width: "150px",
    paddingBottom: "3px !important"
  },
  eventName: {
    flex: "2",
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },
  marketName: {
    display: "flex",
    flex: 1,
    alignItems: "center",
    fontSize: "9px !important"
  },
  badgeDetails: {
    width: "159px",
    display: "flex",
    flexDirection: "column",
    borderRight: props => `1px solid ${props.marketBorderColor || "#dcddf"}`
  },
  eventDetails: {
    width: "143px",
    display: "flex",
    flexDirection: "column"
  },
  eventMarketWrapper: {
    width: "100%",
    display: "flex",
    flex: 1,
    flexDirection: "column",
    boxSizing: "border-box"
  },
  listWrapper: {
    flex: "1",
    overflowX: "auto",
    boxSizing: "border-box",
    display: "grid",
    padding: "3px 3px 0",
    gap: "2px",
    gridTemplateColumns: props =>
      props.isCarousel ? "repeat(auto-fit, minmax(210px, 1fr))" : "2fr 2fr",
    gridColumnGap: "3px",
    gridAutoRows: "25px",
    "&  .odds-text": {
      fontSize: "11px"
    },
    "&  .name": {
      fontSize: "10px"
    }
  }
});

export default defineComponent({
  name: "Leaderboard",
  components: {
    ListOption,
    CardHeaderLeaderboard,
    EventName,
    MarketName
  },
  props: {
    marketId: { type: String, required: true },
    eventId: { type: String, required: true }
  },
  setup(props) {
    const carouselContext = injectCarouselContext();
    const { adTemplateBranding, marketBranding } = injectBrand();
    const market = composeMarket(props.marketId);
    const marketOptions = composeListMarketOptions(props.marketId);
    const event = composeEvent(props.eventId);
    const useCompetitors = computed(() => {
      return (
        event.value.competitors.home.name.full?.length > 0 &&
        event.value.competitors.away.name.full?.length > 0
      );
    });

    const classesRef = useStyles(
      computed(() => ({
        ...adTemplateBranding.value,
        isCarousel: carouselContext.value.isCarousel
      }))
    );

    return {
      market,
      marketBranding,
      useCompetitors,
      classes: classesRef,
      options: marketOptions,
      event
    };
  }
});
</script>
